import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for managing backup reminders
 * @param {Object} options - Configuration options
 * @param {Object} options.formData - Form data to check if user has content
 * @param {boolean} options.isMobile - Whether this is for mobile or desktop
 * @param {number} options.reminderInterval - Minutes between reminders (default: 8 for desktop, 15 for mobile)
 * @param {number} options.activityTimeout - Seconds of recent activity required (default: 30 for desktop, 60 for mobile)
 * @param {number} options.autoHideDelay - Milliseconds to auto-hide mobile badge (mobile only)
 * @param {string} options.storageKey - localStorage key for tracking last shown time
 * @returns {Object} State and handlers for backup reminder
 */
export const useBackupReminder = ({
  formData,
  isMobile = false,
  reminderInterval = isMobile ? 15 : 8,
  activityTimeout = isMobile ? 60 : 30,
  autoHideDelay = 10000,
  storageKey = isMobile ? 'mobileBackupReminderLastShown' : 'backupReminderLastShown'
}) => {
  const [showReminder, setShowReminder] = useState(false);
  const [reminderAnchor, setReminderAnchor] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const reminderShownRef = useRef(false);
  const autoHideTimeoutRef = useRef(null);

  // Activity tracking and reminder logic
  useEffect(() => {
    const checkBackupReminder = () => {
      const now = Date.now();
      const lastShown = localStorage.getItem(storageKey);
      const lastShownTime = lastShown ? parseInt(lastShown) : 0;
      const timeSinceLastShown = now - lastShownTime;
      const timeSinceLastActivity = now - lastActivityRef.current;
      
      // Show reminder if conditions are met
      const shouldShow = 
        timeSinceLastShown > reminderInterval * 60 * 1000 && // Interval check
        timeSinceLastActivity < activityTimeout * 1000 && // Recent activity check
        !reminderShownRef.current && // Not already shown in session
        formData && Object.keys(formData).length > 0; // Has form data

      if (shouldShow) {
        setShowReminder(true);
        reminderShownRef.current = true;
        localStorage.setItem(storageKey, now.toString());
        
        // Auto-hide for mobile after delay
        if (isMobile && autoHideDelay > 0) {
          autoHideTimeoutRef.current = setTimeout(() => {
            setShowReminder(false);
          }, autoHideDelay);
        }
      }
    };

    // Track user activity
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Set up activity listeners based on platform
    const activityEvents = isMobile 
      ? ['touchstart', 'touchmove']
      : ['mousedown', 'keydown', 'scroll'];

    // Check interval (less frequent on mobile)
    const checkInterval = isMobile ? 60000 : 30000; // 1 minute vs 30 seconds
    const interval = setInterval(checkBackupReminder, checkInterval);
    
    // Add activity listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearInterval(interval);
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
      }
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [formData, isMobile, reminderInterval, activityTimeout, autoHideDelay, storageKey]);

  // Handlers
  const showReminderWithAnchor = (anchorElement) => {
    setReminderAnchor(anchorElement);
    setShowReminder(true);
  };

  const hideReminder = () => {
    setShowReminder(false);
    setReminderAnchor(null);
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
    }
  };

  const handleSaveAction = (saveCallback) => {
    if (typeof saveCallback === 'function') {
      saveCallback();
    }
    hideReminder();
  };

  const handleUserInteraction = () => {
    // Hide reminder when user interacts with save-related UI
    if (isMobile && showReminder) {
      hideReminder();
    }
  };

  return {
    // State
    showReminder,
    reminderAnchor,
    
    // Handlers
    showReminderWithAnchor,
    hideReminder,
    handleSaveAction,
    handleUserInteraction,
    
    // Utilities
    setReminderAnchor,
  };
};

export default useBackupReminder; 